import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { useDriverShiftBooking, SHIFT_SLOTS, ShiftSlotId } from "@/hooks/useDriverShiftBooking";
import { useToast } from "@/hooks/use-toast";
import { Calendar, Clock, AlertTriangle, Check, X, Loader2, Sun, Moon, Trash2, CheckCircle2 } from "lucide-react";
import { format } from "date-fns";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";

interface DriverShiftBookingProps {
  forceShow?: boolean;
  onClose?: () => void;
}

interface SelectedShift {
  date: string;
  dateLabel: string;
  slotId: ShiftSlotId;
  slotLabel: string;
  start: string;
  end: string;
  rate: number;
}

export const DriverShiftBooking = ({ forceShow = false, onClose }: DriverShiftBookingProps) => {
  const { toast } = useToast();
  const [selectedShifts, setSelectedShifts] = useState<SelectedShift[]>([]);
  const [confirmDialog, setConfirmDialog] = useState(false);
  
  const [cancelDialog, setCancelDialog] = useState<{
    open: boolean;
    shiftId: string;
    date: string;
    slotLabel: string;
    dateLabel: string;
    shiftData: { shift_date: string; start_time: string; end_time: string };
  } | null>(null);
  
  const {
    driverId,
    driverName,
    remainingShiftsCount,
    shouldPromptForBooking,
    isUrgent,
    isCritical,
    availableSlots,
    myUpcomingShifts,
    bookMultipleShifts,
    cancelShift,
    isLoading,
    isBooking,
  } = useDriverShiftBooking();
  
  // Don't show if not a driver or not needed
  if (!driverId) return null;
  if (!forceShow && !shouldPromptForBooking) return null;
  
  const toggleShiftSelection = (shift: SelectedShift) => {
    setSelectedShifts(prev => {
      const exists = prev.find(s => s.date === shift.date && s.slotId === shift.slotId);
      if (exists) {
        return prev.filter(s => !(s.date === shift.date && s.slotId === shift.slotId));
      } else {
        return [...prev, shift];
      }
    });
  };
  
  const isShiftSelected = (date: string, slotId: ShiftSlotId) => {
    return selectedShifts.some(s => s.date === date && s.slotId === slotId);
  };

  const handleCancelClick = (shiftId: string, date: string, slotId: string, slotLabel: string, dateLabel: string, shiftData: any) => {
    setCancelDialog({ open: true, shiftId, date, slotLabel, dateLabel, shiftData });
  };
  
  const handleConfirmBookAll = async () => {
    if (selectedShifts.length === 0) return;
    
    try {
      const shiftsToBook = selectedShifts.map(s => ({ date: s.date, slotId: s.slotId }));
      const result = await bookMultipleShifts(shiftsToBook);
      
      if (result.success > 0) {
        toast({
          title: `${result.success} Shift${result.success > 1 ? 's' : ''} Booked! ✅`,
          description: result.failed > 0 
            ? `${result.failed} shift${result.failed > 1 ? 's' : ''} could not be booked (may have been taken).`
            : `You've been scheduled for ${result.success} shift${result.success > 1 ? 's' : ''}.`,
          variant: result.failed > 0 ? "destructive" : "default",
        });
      } else {
        toast({
          title: "Booking Failed",
          description: "Could not book any shifts. They may have been taken.",
          variant: "destructive",
        });
      }
      
      setSelectedShifts([]);
      setConfirmDialog(false);
    } catch (error: any) {
      toast({
        title: "Booking Failed",
        description: error.message || "Could not book shifts.",
        variant: "destructive",
      });
      setConfirmDialog(false);
    }
  };

  const handleConfirmCancel = async () => {
    if (!cancelDialog) return;
    
    try {
      await cancelShift(cancelDialog.shiftId, cancelDialog.shiftData);
      toast({
        title: "Shift Cancelled",
        description: `Your ${cancelDialog.slotLabel} on ${cancelDialog.dateLabel} has been cancelled.`,
        variant: "destructive",
      });
      setCancelDialog(null);
    } catch (error: any) {
      toast({
        title: "Cancellation Failed",
        description: error.message || "Could not cancel this shift.",
        variant: "destructive",
      });
      setCancelDialog(null);
    }
  };
  
  
  
  if (isLoading) {
    return (
      <Card className="border-primary/30 bg-gradient-to-br from-primary/5 to-transparent">
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64 mt-2" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <Skeleton key={i} className="h-20 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <>
      <Card className={`shadow-lg ${isCritical ? 'border-destructive/50 bg-gradient-to-br from-destructive/10 to-transparent' : isUrgent ? 'border-warning/50 bg-gradient-to-br from-warning/10 to-transparent' : 'border-primary/40 bg-gradient-to-br from-primary/10 to-transparent'}`}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className={`p-2 rounded-lg ${isCritical ? 'bg-destructive/20' : isUrgent ? 'bg-warning/20' : 'bg-primary/20'}`}>
                <Calendar className={`h-5 w-5 ${isCritical ? 'text-destructive' : isUrgent ? 'text-warning' : 'text-primary'}`} />
              </div>
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  Book Your Shifts
                  {isCritical ? (
                    <Badge variant="destructive" className="animate-pulse">
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      {remainingShiftsCount === 0 ? 'No shifts!' : `${remainingShiftsCount} shift left`}
                    </Badge>
                  ) : isUrgent ? (
                    <Badge variant="destructive" className="bg-warning text-warning-foreground">
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      {remainingShiftsCount} shifts left
                    </Badge>
                  ) : remainingShiftsCount < 5 ? (
                    <Badge variant="secondary">
                      {remainingShiftsCount} shifts scheduled
                    </Badge>
                  ) : null}
                </CardTitle>
                <CardDescription>
                  Select multiple shifts and confirm all at once
                </CardDescription>
              </div>
            </div>
            {onClose && (
              <Button variant="ghost" size="icon" onClick={onClose}>
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {isCritical && (
            <Alert variant="destructive" className="bg-destructive/10 border-destructive/30">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                🚨 <strong>CRITICAL:</strong> You have {remainingShiftsCount === 0 ? 'NO' : `only ${remainingShiftsCount}`} shift{remainingShiftsCount !== 1 ? 's' : ''} remaining! 
                Please book shifts immediately to continue working.
              </AlertDescription>
            </Alert>
          )}
          
          {isUrgent && !isCritical && (
            <Alert variant="destructive" className="bg-warning/10 border-warning/30">
              <AlertTriangle className="h-4 w-4 text-warning" />
              <AlertDescription className="text-warning-foreground">
                ⚠️ You only have <strong>{remainingShiftsCount}</strong> shift{remainingShiftsCount !== 1 ? 's' : ''} remaining. 
                Please book your shifts for the upcoming week!
              </AlertDescription>
            </Alert>
          )}
          
          <ScrollArea className="h-[400px] pr-4">
            <div className="space-y-3">
              {availableSlots.map(({ date, dateLabel, slots }) => (
                <div 
                  key={date} 
                  className="border rounded-lg p-3 bg-card/50 hover:bg-card transition-colors"
                >
                  <div className="font-medium text-sm mb-2 flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    {dateLabel}
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {slots.map(slot => {
                      const myShift = slot.isMyBooking ? myUpcomingShifts.find(s => 
                        s.shift_date === date && s.start_time.startsWith(slot.start)
                      ) : null;
                      
                      const isSelected = isShiftSelected(date, slot.slotId);
                      
                      return (
                        <div
                          key={`${date}-${slot.slotId}`}
                          className={`
                            flex items-center justify-between p-2 rounded-md border transition-all
                            ${slot.isBooked 
                              ? slot.isMyBooking 
                                ? 'bg-success/10 border-success/30' 
                                : 'bg-muted/50 border-muted'
                              : isSelected
                                ? 'bg-primary/20 border-primary ring-2 ring-primary/50'
                                : 'bg-background border-border hover:border-primary/50 cursor-pointer'
                            }
                          `}
                          onClick={() => {
                            if (!slot.isBooked) {
                              toggleShiftSelection({
                                date,
                                dateLabel,
                                slotId: slot.slotId,
                                slotLabel: slot.label,
                                start: slot.start,
                                end: slot.end,
                                rate: slot.rate,
                              });
                            }
                          }}
                        >
                          <div className="flex items-center gap-2">
                            {!slot.isBooked && (
                              <Checkbox 
                                checked={isSelected}
                                onCheckedChange={() => {
                                  toggleShiftSelection({
                                    date,
                                    dateLabel,
                                    slotId: slot.slotId,
                                    slotLabel: slot.label,
                                    start: slot.start,
                                    end: slot.end,
                                    rate: slot.rate,
                                  });
                                }}
                                onClick={(e) => e.stopPropagation()}
                                className="mr-1"
                              />
                            )}
                            {slot.slotId === "evening" ? (
                              <Moon className="h-4 w-4 text-indigo-500" />
                            ) : (
                              <Sun className="h-4 w-4 text-amber-500" />
                            )}
                            <div>
                              <div className="text-sm font-medium">{slot.label}</div>
                              <div className="text-xs text-muted-foreground flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {slot.start} - {slot.end}
                              </div>
                            </div>
                          </div>
                          
                          {slot.isBooked ? (
                            slot.isMyBooking ? (
                              <div className="flex items-center gap-1">
                                <Badge className="bg-success/20 text-success border-success/30">
                                  <Check className="h-3 w-3 mr-1" />
                                  Booked
                                </Badge>
                                {myShift && (
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-7 w-7 text-destructive hover:bg-destructive/10"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleCancelClick(
                                        myShift.id, 
                                        date, 
                                        slot.slotId, 
                                        slot.label, 
                                        dateLabel,
                                        { shift_date: date, start_time: slot.start, end_time: slot.end }
                                      );
                                    }}
                                    disabled={isBooking}
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                )}
                              </div>
                            ) : (
                              <Badge variant="secondary" className="text-xs">
                                {slot.bookedBy}
                              </Badge>
                            )
                          ) : (
                            isSelected && (
                              <Badge className="bg-primary/20 text-primary border-primary/30">
                                <CheckCircle2 className="h-3 w-3 mr-1" />
                                Selected
                              </Badge>
                            )
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
          
          <div className="text-xs text-muted-foreground text-center pt-2 border-t">
            💡 Tap on shifts to select them, then confirm all at once.
          </div>
        </CardContent>
      </Card>
      
      {/* Floating action bar when shifts are selected */}
      {selectedShifts.length > 0 && (
        <div className="fixed bottom-4 left-4 right-4 z-50 animate-in slide-in-from-bottom-4">
          <div className="bg-primary text-primary-foreground rounded-lg shadow-xl p-4 flex items-center justify-between max-w-2xl mx-auto">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 rounded-full h-10 w-10 flex items-center justify-center font-bold text-lg">
                {selectedShifts.length}
              </div>
              <div>
                <div className="font-semibold">
                  {selectedShifts.length} shift{selectedShifts.length > 1 ? 's' : ''} selected
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                size="sm"
                className="text-primary-foreground hover:bg-white/20"
                onClick={() => setSelectedShifts([])}
              >
                Clear
              </Button>
              <Button 
                variant="secondary"
                size="sm"
                className="bg-white text-primary hover:bg-white/90"
                onClick={() => setConfirmDialog(true)}
                disabled={isBooking}
              >
                {isBooking ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Booking...
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Confirm All
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Batch Booking Confirmation Dialog */}
      <Dialog open={confirmDialog} onOpenChange={(open) => !open && setConfirmDialog(false)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-primary" />
              Confirm {selectedShifts.length} Shift{selectedShifts.length > 1 ? 's' : ''}
            </DialogTitle>
            <DialogDescription>
              You're about to book the following shifts:
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <ScrollArea className="max-h-[300px]">
              <div className="space-y-2">
                {selectedShifts.map((shift, idx) => (
                  <div key={`${shift.date}-${shift.slotId}`} className="bg-muted/50 rounded-lg p-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {shift.slotId === "evening" ? (
                        <Moon className="h-4 w-4 text-indigo-500" />
                      ) : (
                        <Sun className="h-4 w-4 text-amber-500" />
                      )}
                      <div>
                        <div className="text-sm font-medium">{shift.dateLabel}</div>
                        <div className="text-xs text-muted-foreground">
                          {shift.slotLabel} ({shift.start} - {shift.end})
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
            
          </div>
          
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setConfirmDialog(false)}
              disabled={isBooking}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmBookAll}
              disabled={isBooking}
              className="bg-success hover:bg-success/90"
            >
              {isBooking ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Booking...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Book All {selectedShifts.length} Shifts
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancellation Confirmation Dialog */}
      <Dialog open={!!cancelDialog?.open} onOpenChange={(open) => !open && setCancelDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="h-5 w-5" />
              Cancel Shift?
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel this shift? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Date:</span>
                <span className="font-medium">{cancelDialog?.dateLabel}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Shift:</span>
                <span className="font-medium">{cancelDialog?.slotLabel}</span>
              </div>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setCancelDialog(null)}
              disabled={isBooking}
            >
              Keep Shift
            </Button>
            <Button 
              variant="destructive"
              onClick={handleConfirmCancel}
              disabled={isBooking}
            >
              {isBooking ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Cancelling...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Cancel Shift
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
